import { area } from "@turf/turf";
import { GeoJSONFeatureCollection, Status } from "./clip-ui/models";
import { FORMAT_LABELS, PublishFormat } from "./clip-ui/format-labels";


export const isViewOnly = (status?: Status): boolean =>
  status === "Quoting" || status === "Processing" || status === "Completed";

export const QUOTE_VALID_SECONDS = 5 * 24 * 60 * 60;

export const hasLiveQuote = (clip: {
  status?: Status;
  quotedOn?: number;
}): boolean => {
  if (clip.status !== "Quoted") return false;
  const age = Math.floor(Date.now() / 1000) - (clip.quotedOn ?? 0);
  return age <= QUOTE_VALID_SECONDS;
};

/**
 * Formats a clip output format code into its human-readable label.
 * Accepts the lowercase wire value (e.g. "gpkg") and tolerates legacy
 * uppercase values (e.g. "SHP") stored on old clips. Defaults to GeoPackage.
 */
export const formatFileFormat = (format?: string): string => {
  if (!format) return FORMAT_LABELS.gpkg;
  const key = format.toLowerCase() as PublishFormat;
  return FORMAT_LABELS[key] ?? format;
};

/**
 * Formats the total area of a clip geometry into a human-readable string
 * @param geometry - The GeoJSON FeatureCollection representing the clip geometry
 * @returns Formatted area string with appropriate units (m² or km²)
 */
export const formatClipArea = (geometry?: GeoJSONFeatureCollection): string => {
  if (!geometry || !geometry.features || geometry.features.length === 0) {
    return "0.0 m²";
  }

  // Calculate total area from all features
  const totalAreaSqm = geometry.features.reduce(
    (sum, feature) => sum + area(feature),
    0
  );

  // Humanize the area - use km² for areas >= 1 km²
  const areaSqkm = totalAreaSqm / 1000000;
  if (areaSqkm < 1) {
    return `${totalAreaSqm.toLocaleString(undefined, {
      maximumFractionDigits: 1,
    })} m²`;
  }
  return `${areaSqkm.toLocaleString(undefined, {
    maximumFractionDigits: 1,
  })} km²`;
};

/**
 * Formats a CRS/EPSG code into a human-readable coordinate system name
 * @param crs - The CRS code (e.g., "EPSG:7844")
 * @returns Human-readable coordinate system name (e.g., "GDA 2020")
 */
export const formatCRS = (crs?: string): string => {
  if (!crs) return "GDA 2020";

  const crsMap: Record<string, string> = {
    "EPSG:4283": "GDA 94",
    "EPSG:7844": "GDA 2020",
    "EPSG:3857": "Web Mercator",
    "EPSG:4326": "WGS 84",
  };

  return crsMap[crs] || crs;
};

/**
 * Maps clip status to MUI Chip color (used by admin pages)
 */
export const getStatusChipColor = (
  status: Status
): "default" | "primary" | "secondary" | "error" | "warning" | "info" | "success" => {
  switch (status) {
    case "Completed":
      return "success";
    case "Failed":
      return "error";
    case "Processing":
      return "info";
    case "Draft":
      return "secondary";
    case "Quoting":
      return "warning";
    case "Quoted":
      return "primary";
    default:
      return "info";
  }
};

/**
 * Maps clip status to Badge styling classes
 */
export const getStatusColor = (status: Status): string => {
  switch (status) {
    case "Completed":
      return "border-green-500 text-green-700 bg-green-50";
    case "Failed":
      return "border-red-500 text-red-700 bg-red-50";
    case "Processing":
      return "border-blue-500 text-blue-700 bg-blue-50";
    case "Draft":
      return "border-gray-400 text-gray-600 bg-gray-50";
    case "Quoting":
      return "border-amber-500 text-amber-700 bg-amber-50";
    case "Quoted":
      return "border-blue-500 text-blue-700 bg-blue-50";
    default:
      return "border-blue-500 text-blue-700 bg-blue-50";
  }
};
